/**
 * @file 学習者の plan を読み、編集 (draft) と保存を扱う hook。
 *
 * @what: サーバの plan を draft (current_stage_id / note / unit_states) に展開し、
 *   チップのクリックや入力で draft を更新、保存で PUT して結果を反映する。
 * @why: 読込結果は「どの学習者のものか」(personId) を持たせ、学習者を切り替えた直後に
 *   前の学習者の plan / draft が「ready」のまま見える瞬間を作らない (その瞬間に保存を
 *   押すと前の学習者の draft が新しい学習者に書き込まれるため)。
 * @failure: 読込失敗は status "error"。保存失敗は saveError に文言を入れ draft は保持する
 *   (内部詳細は console)。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchStudyPlan,
  putStudyPlan,
  type StudyMaterialInput,
  type StudyPlan,
  type StudyPlanPutRequest,
  type StudyUnitStateInput,
} from "../api/plans";

export type StudyPlanDraft = {
  currentStageId: string;
  note: string;
  unitStates: Record<string, StudyUnitStateInput>;
  /** 登録順を保持する。 */
  materials: StudyMaterialInput[];
  /** 用意チェック。true のみ持つ (外すとキーごと消す)。 */
  prepStates: Record<string, boolean>;
  /** うんどうチェックのお気に入り (遊び id、表示順を保つ)。 */
  motorFavorites: string[];
  /** 「終わりまでにできていてほしいこと」の達成チェック。true のみ持つ (達成日はサーバが付ける)。 */
  exitStates: Record<string, boolean>;
};

export type StudyPlanState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; plan: StudyPlan | null };

/** 読込結果。personId 付きで持ち、現在選択中の学習者と一致するときだけ有効とみなす。 */
type LoadResult =
  | { status: "loading" }
  | { status: "error"; personId: string | null }
  | { status: "ready"; personId: string | null; plan: StudyPlan | null };

function draftFrom(plan: StudyPlan | null): StudyPlanDraft {
  const unitStates: Record<string, StudyUnitStateInput> = {};
  const materials: StudyMaterialInput[] = [];
  const prepStates: Record<string, boolean> = {};
  const exitStates: Record<string, boolean> = {};
  const motorFavorites = [...(plan?.motor_favorites ?? [])];
  if (plan) {
    for (const [id, st] of Object.entries(plan.unit_states)) {
      unitStates[id] = st.note ? { status: st.status, note: st.note } : { status: st.status };
    }
    for (const m of plan.materials ?? []) {
      const input: StudyMaterialInput = {
        id: m.id,
        title: m.title,
        status: m.status,
        unit_ids: [...m.unit_ids].sort(),
      };
      if (m.memo) input.memo = m.memo;
      if (m.total_pages) input.total_pages = m.total_pages;
      if (m.current_page) input.current_page = m.current_page;
      if (m.stage_id) input.stage_id = m.stage_id;
      if (m.realm) input.realm = m.realm;
      if (m.isbn) input.isbn = m.isbn;
      if (m.publisher) input.publisher = m.publisher;
      if (m.url) input.url = m.url;
      materials.push(input);
    }
    for (const [id, v] of Object.entries(plan.prep_states ?? {})) {
      if (v) prepStates[id] = true;
    }
    for (const id of Object.keys(plan.exit_states ?? {})) {
      exitStates[id] = true;
    }
  }
  return {
    currentStageId: plan?.current_stage_id ?? "",
    note: plan?.note ?? "",
    unitStates,
    materials,
    prepStates,
    motorFavorites,
    exitStates,
  };
}

function toRequest(d: StudyPlanDraft): StudyPlanPutRequest {
  return {
    current_stage_id: d.currentStageId,
    note: d.note,
    unit_states: d.unitStates,
    // unit_ids はサーバの正規形 (ソート済み) に合わせて送る (保存後の false-dirty 防止)
    materials: d.materials.map((m) => ({ ...m, unit_ids: [...m.unit_ids].sort() })),
    prep_states: d.prepStates,
    motor_favorites: d.motorFavorites,
    exit_states: d.exitStates,
  };
}

export function useStudyPlan(familyId: string, personId: string | null) {
  const [loaded, setLoaded] = useState<LoadResult>({ status: "loading" });
  const [draft, setDraft] = useState<StudyPlanDraft>(draftFrom(null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) {
      setLoaded({ status: "ready", personId: null, plan: null });
      setDraft(draftFrom(null));
      return;
    }
    const controller = new AbortController();
    setLoaded({ status: "loading" });
    setSaveError(null);
    setSavedAt(null);
    fetchStudyPlan(familyId, personId, controller.signal)
      .then((plan) => {
        if (controller.signal.aborted) return;
        setLoaded({ status: "ready", personId, plan });
        setDraft(draftFrom(plan));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load plan", err);
        setLoaded({ status: "error", personId });
      });
    return () => controller.abort();
  }, [familyId, personId]);

  // 選択中の学習者の結果だけを state として見せる (切替直後は loading 扱い)。
  const state: StudyPlanState = useMemo(() => {
    if (loaded.status === "loading" || loaded.personId !== personId) return { status: "loading" };
    if (loaded.status === "error") return { status: "error" };
    return { status: "ready", plan: loaded.plan };
  }, [loaded, personId]);

  const baseline = useMemo(
    () => JSON.stringify(toRequest(draftFrom(state.status === "ready" ? state.plan : null))),
    [state],
  );
  const dirty = state.status === "ready" && JSON.stringify(toRequest(draft)) !== baseline;

  const save = useCallback(async () => {
    if (!personId || saving || state.status !== "ready") return;
    setSaving(true);
    setSaveError(null);
    try {
      const plan = await putStudyPlan(familyId, personId, toRequest(draft));
      setLoaded({ status: "ready", personId, plan });
      setDraft(draftFrom(plan));
      setSavedAt(plan.updated_at);
    } catch (err: unknown) {
      console.error("[study] failed to save plan", err);
      setSaveError("保存できませんでした。時間を置いて再度お試しください。");
    } finally {
      setSaving(false);
    }
  }, [draft, familyId, personId, saving, state.status]);

  /**
   * 記録の POST でサーバが plan.materials[].current_page を前進させた分を、baseline と
   * draft の両方へ同じ規則 (max、後退しない) で反映する。plan の再 fetch をしないのは
   * 未保存の draft を消さないため。
   */
  const applyMaterialPageBump = useCallback((materialId: string, toPage: number) => {
    if (toPage <= 0) return;
    const bump = <T extends { id: string; current_page?: number }>(list: T[]): T[] =>
      list.map((m) =>
        m.id === materialId && toPage > (m.current_page ?? 0) ? { ...m, current_page: toPage } : m,
      );
    setLoaded((prev) => {
      if (prev.status !== "ready" || !prev.plan) return prev;
      return { ...prev, plan: { ...prev.plan, materials: bump(prev.plan.materials ?? []) } };
    });
    setDraft((prev) => ({ ...prev, materials: bump(prev.materials) }));
  }, []);

  return {
    state,
    draft,
    setDraft,
    dirty,
    saving,
    saveError,
    savedAt,
    save,
    applyMaterialPageBump,
  };
}
